/**
 * Field names attached by flame utilities to each datapoint before handing the
 * row to XYChart. Kept as constants so accessors passed to XYChart and reads
 * inside tooltips stay in lockstep.
 */

import { units } from '@dynatrace-sdk/units';

/**
 * Depth of the frame in the call tree (root = 0).
 */
export const DEPTH_FIELD = 'depth';

/**
 * Index into the original `data` array; used to route tooltip actions back to the caller's row.
 */
export const FLAME_ORIGINAL_IDX = '__flameOriginalIdx';

/**
 * Derived span duration: `endAccessor - startAccessor`.
 */
export const VALUE_FIELD = '__value';

/**
 * Normalized start: `startAccessor - minTime`.
 */
export const START_FIELD = '__start';

/**
 * Normalized end: `endAccessor - minTime`.
 */
export const END_FIELD = '__end';

/**
 * Number of direct children of a node in the aggregated flame graph.
 */
export const CHILDREN_COUNT_FIELD = 'childrenCount';

/**
 * Normalized self value: the span's exclusive time (or sample count), written
 * by `aggregateSpansToFlameGraph` and `orderSpansHierarchically` so downstream
 * readers are independent of the raw accessor path.
 */
export const SELF_VALUE_FIELD = '__selfValue';

export const XY_DEFAULTS = {
  x0Accessor: 'x0',
  x1Accessor: 'x1',
  xAxisId: 'flame-x-axis',
  yAxisId: 'flame-y-axis',
} as const;

export const DISPLAY_NAME_FLAME_CHART = 'FlameChart';
export const DISPLAY_NAME_FLAME_GRAPH = 'FlameGraph';

/** Default chart height in pixels when the consumer passes a non-pixel height value. */
export const DEFAULT_CHART_HEIGHT = 300;

/**
 * Vertical space consumed by the FlameChart's x-axis row plus one OVERLAY_PADDING.
 * = worst-case single-line x-axis height (20px) + OVERLAY_PADDING (7px).
 */
export const FLAME_CHART_HEIGHT_OVERHEAD_PX = 27;

/**
 * Vertical space consumed by the FlameGraph when both axes are hidden.
 * Equals 2 × OVERLAY_PADDING (7px each side) = 14px.
 */
export const FLAME_GRAPH_HEIGHT_OVERHEAD_PX = 14;

/** Fixed height in pixels for each flame bar row. */
export const FRAME_HEIGHT_PX = 16;

export const DEFAULT_FLAME_CHART_UNIT = units.time.millisecond;
export const DEFAULT_FLAME_GRAPH_UNIT = units.unspecified.count;
